/**
 * [BaselineItemList ] Table/List view for items within a Baseline (Step 3A of Baseline workflow).
 * - Renders all items belonging to the current baseline in a sortable, filterable table.
 * - Handles row selection, search/filtering, paging, and sort via the useListTableState custom hook.
 * - Displays key attributes: Name, Option Type, Military Service, Domain, Category, Status, and Last Updated.
 * - Passes item selection or status changes upward via props.
 * - Does not mutate Redux state directly; all changes are local or sent to parent.
 * - RAVEN design: Table UX logic is DRY and consistent with BaselineList (table state via shared custom hook).
 *
 * @component
 * @param {Object} props
//  * @param {Object} props.item - The current baseline (with item list).
 * @param {string} props.mode - Controls editability ("edit", "view", etc.).
 * @param {function(Object):void} props.onSelectBaselineItem - Callback when an item row is clicked.
 * @param {function(number, number):void} props.onChangeStatus - Callback for archive/reactivate item.
 * @param {function(number):void} props.onAddItem - Callback to add a new item.
 * @param {function(number):void} props.onSetRowsPerPage - Callback for rows per page change.
 * @returns {React.ReactElement}
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Parser } from "html-to-react";
import { Buffer } from "buffer";
import PropTypes from "prop-types";

// Custom hooks
import { useListTableState } from "src/hooks/useListTableState";
import { useLookupsData } from "src/context/LookupsContext";

// Layout/UI
import {
	Box,
	ButtonGroup,
	Checkbox,
	createListCollection,
	IconButton,
	MetreaSelectContent,
	MetreaSelectItem,
	MetreaSelectRoot,
	MetreaSelectTrigger,
	MetreaSelectValueText,
	Pagination,
	Portal,
	Switch,
	Table,
	MetreaTooltip,
} from "@metrea-ui/v2";
import styles from "./BaselineList.module.css";
import RecordNav from "src/components/UI/RecordNav/RecordNav";
import { EnhancedTableHead, EnhancedTableToolbar } from "src/components/UI/common/EnhancedTableParts";
import CardList from "src/components/UI/CardList/CardList";
import { EraserRegular, ChevronLeftRegular, ChevronRightRegular } from "@fluentui/react-icons";

// Utilities
import { deepClone, findObjectByProperty } from "src/utils/dataUtils";
import { isDevTeam, isDeveloper } from "src/utils/sessionUtils";
import { debugLog, showTesting, isDevSiteNow } from "src/utils/debugUtils";
import { getShortDateTimeString, getShortDateString, highlight } from "src/utils/formatUtils";
import { getBaselineItemDataStatusLabel } from "src/utils/baselineUtils";

const SEARCH_FIELDS = ["name", "option_type", "military_service", "domain", "category", "data_status"];

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const statusCollection = createListCollection({
	items: [
		{ label: "Inactive", value: "0", rawValue: 0 },
		{ label: "Active", value: "1", rawValue: 1 },
	],
});

const BaselineItemList = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const htmlParser = new Parser();

	// --- Redux Selectors ---
	const selectedBaseline = useSelector((state) => state.baselines.selectedBaseline);

	// --- Local State ---
	const [currBaseline, setCurrBaseline] = useState(selectedBaseline);
	const [baselineItemList, setBaselineItemList] = useState(selectedBaseline?.baseline_items ?? []);
	const [showInactive, setShowInactive] = useState(false);
	const [itemSelected, setItemSelected] = useState([]);
	const [searchKeyword, setSearchKeyword] = useState("");

	useEffect(() => {
		debugLog(2, "[BaselineItemList][FLOW] mounted", {});
	}, []);

	// Custom hooks
	const { lookups } = useLookupsData();

	// Helper to return filtered scenarios based on Active/Inactive toggle.
	// Precompute search-friendly strings for every row
	const enrichedItems = useMemo(() => {
		if (!currBaseline || !lookups) return baselineItemList;

		return (baselineItemList ?? []).map((row) => {
			const optionTypeName =
				findObjectByProperty(lookups.BL_ITEM_OPT_TYPES, "id", parseInt(row.option_type_id))?.name ??
				"";

			const militaryServiceName =
				findObjectByProperty(currBaseline.baseline_configs, "id", parseInt(row.military_service_id))
					?.comp_value ?? "";

			const domainName =
				findObjectByProperty(currBaseline.baseline_configs, "id", parseInt(row.domain_id))
					?.comp_value ?? "";

			const categoryName =
				findObjectByProperty(currBaseline.baseline_configs, "id", parseInt(row.category_id))
					?.comp_value ?? "";

			const dataStatusLabel = getBaselineItemDataStatusLabel(row?.data_status_id);

			return {
				...row,
				option_type: optionTypeName,
				military_service: militaryServiceName,
				domain: domainName,
				category: categoryName,
				data_status: dataStatusLabel,
				_searchFields: {
					name: row.name ?? "",
					option_type: optionTypeName,
					military_service: militaryServiceName,
					domain: domainName,
					category: categoryName,
					data_status: dataStatusLabel,
				},
			};
		});
	}, [baselineItemList, currBaseline, lookups]);

	const visibleItems = useMemo(() => {
		const activeFiltered = showInactive ? enrichedItems : enrichedItems.filter((x) => x.status_id === 1);
		if (!searchKeyword) return activeFiltered;

		const kw = searchKeyword.toLowerCase();
		const result = activeFiltered.filter((u) =>
			SEARCH_FIELDS.some((field) => {
				const val = u._searchFields?.[field];
				return typeof val === "string" && val.toLowerCase().includes(kw);
			}),
		);
		debugLog(4, "[BaselineItemList][PROCESS] visibleItems filter fields", {
			searchFields: SEARCH_FIELDS,
		});

		return result;
	}, [showInactive, enrichedItems, searchKeyword]);

	// --- Controlled selection handler (notifies parent on user change only)
	const handleSelectionChange = (newSelected) => {
		debugLog(3, "[BaselineItemList][ACTION] handleSelectionChange", {
			selectedCount: newSelected?.length ?? 0,
		});
		setItemSelected(newSelected);
	};

	// --- Effect: sync local baseline on selection change ---
	useEffect(() => {
		setCurrBaseline(selectedBaseline);
		if (selectedBaseline?.baseline_items?.length > 0)
			setBaselineItemList([...deepClone(selectedBaseline.baseline_items)]);
		debugLog(3, "[BaselineItemList][STATE] selectedBaseline changed", {
			baselineId: selectedBaseline?.id ?? null,
			itemCount: selectedBaseline?.baseline_items?.length ?? 0,
		});
	}, [selectedBaseline]);

	const imgPlaceholder = "/No_Image_Available.jpg";

	// Normalize byte payload shapes from API so Baseline and Scenario render paths
	// behave consistently across Buffer-like objects and base64 strings.
	const toImageSrc = (thumbnail) => {
		if (!thumbnail) return imgPlaceholder;
		try {
			if (typeof thumbnail === "string") {
				const value = thumbnail.trim();
				if (!value) return imgPlaceholder;
				return value.startsWith("data:image/") ? value : `data:image/*;base64,${value}`;
			}
			if (thumbnail?.data && Array.isArray(thumbnail.data) && thumbnail.data.length > 0) {
				return `data:image/*;base64,${Buffer.from(thumbnail.data).toString("base64")}`;
			}
			// Handle Buffer/Uint8Array-like objects.
			const b64 = Buffer.from(thumbnail).toString("base64");
			return b64 ? `data:image/*;base64,${b64}` : imgPlaceholder;
		} catch (_e) {
			return imgPlaceholder;
		}
	};
	const headCells = [
		{
			id: "thumbnail",
			numeric: false,
			disablePadding: false,
			label: " ",
		},
		{
			id: "name",
			numeric: false,
			disablePadding: false,
			label: "Name",
		},
		{
			id: "option_type_id",
			numeric: false,
			disablePadding: false,
			label: "Option Type",
		},
		{
			id: "military_service_id",
			numeric: false,
			disablePadding: false,
			label: "Military Service",
		},
		{
			id: "domain_id",
			numeric: false,
			disablePadding: false,
			label: "Domain",
		},
		{
			id: "category_id",
			numeric: false,
			disablePadding: false,
			label: "Category",
		},
		{
			id: "updatedAt",
			numeric: false,
			disablePadding: false,
			label: "Last Updated",
		},
		{
			id: "status_id",
			numeric: false,
			disablePadding: false,
			label: "Status",
			align: "center",
		},
	];

	// Custom hooks
	const {
		order,
		setOrder,
		orderBy,
		setOrderBy,
		setPage,
		pageM1,
		setPageM1,
		rowsPerPage,
		setRowsPerPage,
		dense,
		selected,
		visibleRows,
		emptyRows,
		handleRequestSort,
		handleChangePageM1,
		handleChangeDense,
		handleSelectAllClick,
		isSelected,
		checkHandler,
	} = useListTableState({
		items: visibleItems, // the filtered list for active/inactive toggle
		// Default newest-updated items to the top in CardList and keep date sorts
		// based on real timestamps instead of relying on legacy normalized keys.
		initialOrderBy: "updatedAt",
		initialOrder: "desc",
		initialRowsPerPage: 10, // or props.rowsPerPage
		initialPage: 1,
		selectionMode: showTesting() && isDevTeam(), // or true/false as needed
		selected: itemSelected,
		onSelectedChange: handleSelectionChange,
		sortAccessors: {
			updatedAt: (row) => Date.parse(row?.updatedAt ?? "") || 0,
			createdAt: (row) => Date.parse(row?.createdAt ?? "") || 0,
			data_status: (row) => Number(row?.data_status_id ?? 0),
		},
	});

	// --- Handlers ---
	const rowClickHandler = (event, item) => {
		debugLog(3, "[BaselineItemList][ACTION] rowClickHandler", { item: item });
		props.onSelectBaselineItem(item);
	};

	const changeStatusHandler = (o, value) => {
		debugLog(3, "[BaselineItemList][ACTION] changeStatusHandler", {
			id: o.id,
			name: o.name,
			value: value,
			baselineItem: o,
		});
		props.onChangeStatus(o.id, value);
	};

	const searchKeywordChangeHandler = (e, clearSearchKeyword = null) => {
		const sKeyword = clearSearchKeyword ? "" : e.target.value.trim();
		setSearchKeyword(sKeyword);

		let re = new RegExp(sKeyword, "ig");
		setBaselineItemList(
			currBaseline.baseline_items.filter((x) => {
				return x.name.match(re);
			}),
		);

		debugLog(7, "[BaselineItemList][ACTION] searchKeywordChangeHandler", { searchKeyword: sKeyword });
	};

	// Handlers for RecordNav (sorting, show inactive, etc.)
	const handleSearchChange = (keyword) => {
		setSearchKeyword(keyword);
		debugLog(7, "[BaselineItemList][ACTION] handleSearchChange", { keyword: keyword });
	};

	const toggleInactiveHandler = (option) => {
		debugLog(3, "[BaselineItemList][ACTION] toggleInactiveHandler", { option: option });
		setShowInactive(option);
	};

	const addItemHandler = (option) => {
		debugLog(3, "[BaselineItemList][ACTION] addItemHandler", { option: option });
		props.onAddItem(option);
	};

	const sortByHandler = (option) => {
		debugLog(3, "[BaselineItemList][ACTION] sortByHandler", { option: option });
		setOrderBy(option.orderBy);
		setOrder(option.order);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[BaselineItemList][ACTION] changeRowsPerPageHandler", { option: option });
		setRowsPerPage(option.id);
		props.onSetRowsPerPage(option.id);
		setPage(0);
		setPageM1(1);
	};

	const renderStatusSelect = (row) => (
		<MetreaSelectRoot
			variant="outline"
			collection={statusCollection}
			size={sizeToSelectSize(props.size)}
			width="105px"
			name="status_id"
			value={[String(row.status_id ?? "")]}
			onValueChange={(details) => {
				const nextValue = Array.isArray(details?.value) ? String(details.value[0] ?? "") : "";
				// Keep the existing status-change contract: the direct Select gives us
				// strings, but downstream handlers still expect numeric 0/1 ids here.
				const normalizedValue = Number.isNaN(Number(nextValue)) ? nextValue : Number(nextValue);
				changeStatusHandler(row, normalizedValue);
			}}
		>
			<MetreaSelectTrigger id={`baseline-item-status-${row.id}`}>
				<MetreaSelectValueText />
			</MetreaSelectTrigger>
			<Portal>
				<MetreaSelectContent>
					{statusCollection.items.map((item) => (
						<MetreaSelectItem key={item.value} item={item}>
							{item.label}
						</MetreaSelectItem>
					))}
				</MetreaSelectContent>
			</Portal>
		</MetreaSelectRoot>
	);

	// Inside your component (replace the current <Paper>...<Table.Root>...</Paper> block):
	const columns = [
		...(showTesting() && isDevTeam()
			? [
					{
						key: "uuid",
						title: "UUID",
						w: 240,
						render: (row) => row.uuid,
					},
				]
			: []),
		{
			key: "name",
			title: "Name",
			w: 360,
			stackGrow: 2,

			// -------------------------------------------------------
			// Show only the plain Name in the visible cell (no description).
			// -------------------------------------------------------
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.name, false)),

			// -------------------------------------------------------
			// Decide per row when to display a tooltip:
			// - If there is a description → show custom Name+Note tooltip even when not truncated
			// - If description is empty → fall back to normal "auto" (only show when truncated)
			// -------------------------------------------------------
			tooltip: (row) => {
				const hasDescription = Boolean(row.description?.trim().length > 0);
				return hasDescription ? "always" : "auto";
			},

			// -------------------------------------------------------
			// Provide the text shown inside the tooltip:
			// - If there's a description → show:
			//       "<Name>\n\nNote:\n<Description>"
			// - Otherwise → return undefined so CardList skips rendering tooltip entirely.
			// -------------------------------------------------------
			tooltipText: (row) => {
				const descriptionExists = Boolean(row.description?.trim().length > 0);
				if (!descriptionExists) return undefined;

				// return `${row.name}\n\nNote:\n${row.description}`;

				const tooltipLines = [
					row.name,
					"", // blank line for spacing
					"Note:",
					row.description,
				];
				// Join with line breaks so EllipsisTooltip will display multiline correctly
				return tooltipLines.join("\n");
			},
		},
		{
			key: "option_type",
			title: "Option Type",
			minW: 200,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.option_type, false)),
		},
		{
			key: "military_service",
			title: "Military Service",
			minW: 80,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.military_service, false)),
		},
		{
			key: "domain",
			title: "Domain",
			minW: 80,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.domain, false)),
		},
		{
			key: "category",
			title: "Category",
			minW: 80,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.category, false)),
		},
		{
			key: "createdAt",
			title: "Date Created",
			minW: 80,
			tooltip: "auto",
			render: (row) => getShortDateString(row.createdAt),
		},
		{
			key: "updatedAt",
			title: "Last Updated",
			minW: 120,
			tooltip: "auto",
			render: (row) => getShortDateTimeString(row.updatedAt),
		},
		{
			key: "status_id",
			title: "Status",
			minW: 60,
			tooltip: undefined, // or omit the field to disable the ellipsis tooltip path
			render: (row) => (row?.status_id === 1 ? "Active" : "Inactive"),
		},
		{
			key: "data_status",
			title: "Data Status",
			minW: 80,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.data_status, false)),
		},
	];

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[BaselineItemList][FLOW] render", {
		renderCount: renderCount.current,
		currBaseline: currBaseline,
		baselineItemList: baselineItemList,
	});
	debugLog(9, "[BaselineItemList][FLOW] renderState", {
		props: props,
		showInactive: showInactive,
		searchKeyword: searchKeyword,
		baselineItemList: baselineItemList,
		visibleItems: visibleItems,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<Box data-testid="BaselineItemsList" w="100%">
			{/* Replace Table/Paper block with CardList */}
			<CardList
				rows={visibleRows}
				keyField="id"
				columns={columns}
				toolbarSlot={
					<RecordNav
						data={visibleItems}
						showSearch={isDeveloper()}
						searchFields={SEARCH_FIELDS}
						columns={columns}
						onSearchChange={handleSearchChange}
						showToggleInactive
						showSortBy
						showRowsPerPage
						{...(props.mode === "edit" ? { showAddItem: true } : {})}
						rowsPerPage={rowsPerPage}
						count={visibleRows.length}
						onToggleInactive={toggleInactiveHandler}
						onAddItem={addItemHandler}
						onSortBy={(opt) => sortByHandler({ orderBy: opt.orderBy, order: opt.order })}
						onChangeRowsPerPage={(opt) => changeRowsPerPageHandler(opt)}
						callFrom="Listing"
						orderBy={orderBy}
						order={order}
						addItemAdjacentSlot={props.addItemAdjacentSlot}
					/>
				}
				showLabels
				density="compact"
				responsive={{
					mode: "auto",
					maxRows: 5,
					minColWidth: 100,
					gap: 12,
				}}
				showVerticalDividers
				paginationSlot={
					<Pagination.Root
						count={visibleItems.length}
						pageSize={Number(rowsPerPage) || 10}
						page={pageM1}
						onPageChange={({ page }) => handleChangePageM1(undefined, page)}
					>
						<ButtonGroup variant="ghost" size="2xs">
							<Pagination.PrevTrigger asChild>
								<IconButton>
									<ChevronLeftRegular />
								</IconButton>
							</Pagination.PrevTrigger>
							<Pagination.Items
								render={(page) => (
									<IconButton variant={{ base: "ghost", _selected: "solid" }}>
										{page.value}
									</IconButton>
								)}
							/>
							<Pagination.NextTrigger asChild>
								<IconButton>
									<ChevronRightRegular />
								</IconButton>
							</Pagination.NextTrigger>
						</ButtonGroup>
					</Pagination.Root>
				}
				rowActions={(row) => {
					const actions = [];
					const canEdit = props.mode === "edit";
					const canShowTestingActions = canEdit && showTesting() && isDeveloper();

					actions.push({
						id: "open",
						label: "Open",
						onSelect: (row) => rowClickHandler(null, row),
					});

					const isActive = String(row.status_id) === "1";
					if (canEdit) {
						actions.push({
							id: "toggle-active",
							label: isActive ? "Make Inactive" : "Make Active",
							onSelect: () => changeStatusHandler(row, isActive ? 0 : 1),
						});
					}

					if (canShowTestingActions) {
						actions.push({
							id: "divider-destructive",
							type: "separator",
						});
						actions.push({
							id: "soft-delete",
							label: "Soft Delete",
							onSelect: () => props.onSoftDelete?.(row),
						});
						actions.push({
							id: "purge",
							label: "Purge",
							onSelect: () => props.onPurge?.(row),
						});
					}

					return actions;
				}}
				photoColumn={(row) => {
					const src = toImageSrc(row.thumbnail);
					return (
						<img
							src={src}
							alt={row.name}
							style={{
								width: "100px",
								height: "100%",
								objectFit: "contain",
								borderRadius: "8px",
								padding: "5px",
							}}
						/>
					);
				}}
			/>

			{/* Keep the legacy table mirror behind testing mode on the direct v2 table path for now. */}
			{showTesting() && isDeveloper() && (
				<>
					<Box marginTop="600px"></Box>
					<Box mb="10px" display="flex" alignItems="center">
						<input
							type="text"
							className="form-control border-2 border-gray-300 mb-0 w-64"
							placeholder="Search by keywords"
							value={searchKeyword}
							onChange={searchKeywordChangeHandler}
						/>
						<MetreaTooltip content="Clear Search" positioning={{ placement: "right" }}>
							<IconButton variant="ghost" onClick={(e) => searchKeywordChangeHandler(e, true)}>
								<EraserRegular />
							</IconButton>
						</MetreaTooltip>
					</Box>

					<RecordNav
						data={visibleItems}
						showToggleInactive
						// This is for the developers only.
						// 1. There's already a "Add New" button in the CardList toolbar, so we can disable this to avoid confusion.
						// 2. Need to disable "Add New" button to pass the Unit Test.
						// {...(props.mode === "edit" ? { showAddItem: true } : {})}
						showSortBy
						showRowsPerPage
						rowsPerPage={rowsPerPage}
						count={visibleRows.length}
						onToggleInactive={toggleInactiveHandler}
						onAddItem={addItemHandler}
						onSortBy={sortByHandler}
						onChangeRowsPerPage={changeRowsPerPageHandler}
					/>

					<Box
						w="100%"
						mb={3}
						bg="white"
						rounded="md"
						borderWidth="1px"
						borderColor="blackAlpha.200"
						boxShadow="sm"
					>
						{showTesting() && isDevTeam() && (
							<EnhancedTableToolbar numSelected={selected.length} />
						)}
						<Box overflowX="auto">
							<Table.Root
								size={dense ? "sm" : "md"}
								variant="outline"
								aria-labelledby="tableTitle"
							>
								<EnhancedTableHead
									numSelected={selected.length}
									order={order}
									orderBy={orderBy}
									onSelectAllClick={handleSelectAllClick}
									onRequestSort={handleRequestSort}
									rowCount={visibleItems.length}
									headCells={headCells}
									styles={styles}
								/>
								<Table.Body>
									{baselineItemList &&
										visibleRows.map((row, index) => {
											const isItemSelected = isSelected(row.id);
											const labelId = `enhanced-table-checkbox-${index}`;
											let strMilitaryService = "";
											let strDomain = "";
											let strCategory = "";
											let tempObj = {};
											if (row.military_service_id) {
												tempObj = findObjectByProperty(
													currBaseline.baseline_configs,
													"id",
													parseInt(row.military_service_id),
												);
												if (tempObj) strMilitaryService = tempObj.comp_value;
											}
											if (row.domain_id) {
												tempObj = findObjectByProperty(
													currBaseline.baseline_configs,
													"id",
													parseInt(row.domain_id),
												);
												if (tempObj) strDomain = tempObj.comp_value;
											}
											if (row.category_id) {
												tempObj = findObjectByProperty(
													currBaseline.baseline_configs,
													"id",
													parseInt(row.category_id),
												);
												if (tempObj) strCategory = tempObj.comp_value;
											}
											const hasDescription = row.description?.trim?.();
											const thumbnail = (
												<img
													alt="not found"
													width={
														toImageSrc(row.thumbnail) !== imgPlaceholder
															? "160px"
															: "80px"
													}
													src={toImageSrc(row.thumbnail)}
												/>
											);
											const itemName = (
												<div>
													{htmlParser.parse(
														highlight(searchKeyword, row.name, false),
													)}
												</div>
											);

											return (
												<Table.Row
													key={row.id}
													data-selected={isItemSelected}
													style={{ cursor: "pointer" }}
													className={styles.tableBodyRow}
												>
													{showTesting() && isDevTeam() && (
														<Table.Cell>
															<Checkbox.Root
																checked={isItemSelected}
																onCheckedChange={(next) =>
																	checkHandler(
																		{
																			target: {
																				checked:
																					next === true ||
																					next?.checked === true ||
																					next?.detail?.checked ===
																						true,
																			},
																		},
																		row.id,
																	)
																}
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																}}
															>
																<Checkbox.HiddenInput
																	aria-labelledby={labelId}
																/>
																<Checkbox.Control />
															</Checkbox.Root>
														</Table.Cell>
													)}
													<Table.Cell
														component="th"
														id={labelId}
														scope="row"
														// padding="none"
														style={{
															minWidth: "120px",
															width: "20%",
															paddingX: "2%",
														}}
														onClick={(event) => rowClickHandler(event, row)}
													>
														{hasDescription ? (
															<MetreaTooltip content={row.description}>
																{thumbnail}
															</MetreaTooltip>
														) : (
															thumbnail
														)}
													</Table.Cell>

													<Table.Cell
														id={labelId}
														style={{ width: "20%" }}
														onClick={(event) => rowClickHandler(event, row)}
													>
														{hasDescription ? (
															<MetreaTooltip content={row.description}>
																{itemName}
															</MetreaTooltip>
														) : (
															itemName
														)}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
														{findObjectByProperty(
															lookups?.BL_ITEM_OPT_TYPES,
															"id",
															parseInt(row?.option_type_id),
														)?.name ?? ""}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
														{strMilitaryService}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
														{strDomain}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
													>
														{strCategory}
													</Table.Cell>
													<Table.Cell
														onClick={(event) => rowClickHandler(event, row)}
														style={{ fontSize: "0.7rem" }}
													>
														{getShortDateTimeString(row.updatedAt)}
													</Table.Cell>
													<Table.Cell style={{ textAlign: "center" }}>
														{props.mode !== "edit" && row.statusLabel}
														{props.mode === "edit" && renderStatusSelect(row)}
													</Table.Cell>
												</Table.Row>
											);
										})}
									{emptyRows > 0 && (
										<Table.Row style={{ height: (dense ? 33 : 53) * emptyRows }}>
											<Table.Cell
												colSpan={
													(showTesting() && isDevTeam() ? 1 : 0) + headCells.length
												}
												style={{ borderBottom: "none", padding: 0 }}
											/>
										</Table.Row>
									)}
								</Table.Body>
							</Table.Root>
						</Box>
					</Box>

					<Box className="flex-auto justify-items-center p-1">
						<Pagination.Root
							count={visibleItems.length}
							pageSize={Number(rowsPerPage) || 10}
							page={pageM1}
							onPageChange={({ page }) => handleChangePageM1(undefined, page)}
						>
							<ButtonGroup variant="ghost" size="2xs">
								<Pagination.PrevTrigger asChild>
									<IconButton>
										<ChevronLeftRegular />
									</IconButton>
								</Pagination.PrevTrigger>
								<Pagination.Items
									render={(page) => (
										<IconButton variant={{ base: "ghost", _selected: "solid" }}>
											{page.value}
										</IconButton>
									)}
								/>
								<Pagination.NextTrigger asChild>
									<IconButton>
										<ChevronRightRegular />
									</IconButton>
								</Pagination.NextTrigger>
							</ButtonGroup>
						</Pagination.Root>
					</Box>

					{/* Keep your Dense switch for dev/testing */}
					{showTesting() && isDevTeam() && isDevSiteNow() && (
						<Switch.Root
							size="sm"
							className="m-[10px]"
							checked={dense}
							onCheckedChange={(next) =>
								handleChangeDense({
									target: {
										checked:
											next === true ||
											next?.checked === true ||
											next?.detail?.checked === true,
									},
								})
							}
						>
							<Switch.HiddenInput />
							<Switch.Control>
								<Switch.Thumb />
							</Switch.Control>
							<Switch.Label>Dense padding</Switch.Label>
						</Switch.Root>
					)}
				</>
			)}
		</Box>
	);
};

BaselineItemList.propTypes = {
	onSelectBaselineItem: PropTypes.func.isRequired,
	onChangeStatus: PropTypes.func.isRequired,
	onSoftDelete: PropTypes.func,
	onPurge: PropTypes.func,
	mode: PropTypes.string,
	size: PropTypes.string,
	addItemAdjacentSlot: PropTypes.node,
};

export default BaselineItemList;
